import {differenceWith, uniqBy} from 'lodash';
import React, {Dispatch, FC, SetStateAction, useState} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {FrontEvent, Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {BoxedInput} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {boxedInputColorReaders} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {BoxedInputElement} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {TextAreaBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {InputTypesEnum} from '../../../../../../libs/shared/ui/src/components/inputs/input';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {Dialog} from '../../../../../components/common/dialogs/dialog';
import {DialogButtonGroup} from '../../../../../components/common/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../../../components/common/dialogs/dialogContent';
import {DialogHeader} from '../../../../../components/common/dialogs/dialogHeader';
import {OkCancelDialog} from '../../../../../components/common/dialogs/okCancelDialog';
import {RecipientsEditorContainer} from '../../../../../components/common/editor/recipients/recipientsEditorContainer';
import {
  AddRecipients,
  isVisualRecipientEqualToHandle,
  RemoveRecipients,
} from '../../../../../helpers/models/recipientHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {
  AnalyticsMetricsKeys,
  AnalyticsTableMetricsEnum,
} from '../../../../../models/api/analyticsMetricsModel';
import {SchedulingConfig, SchedulingFrequenciesEnum} from '../../../../../models/api/analyticsSavedViewModel';
import {buildContactForHandle, ContactSourcesEnum} from '../../../../../models/api/contactModel';
import {RecipientRolesEnum} from '../../../../../models/api/recipientModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {InterfaceEventsEnum} from '../../../../../services/tracking';
import {makeGetTimezone} from '../../../../../store/data/schedulingLinks/schedulingLinksSelectors';
import {
  getAnalyticsReportType,
  getAnalyticsSelectedNamespace,
} from '../../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {getAllAnalyticsMetricsToFetch} from '../../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {updateSavedViewAsyncAction} from '../../../../../store/panels/analytics/views/analyticsPanelViewsActions';
import {getAnalyticsSelectedView} from '../../../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {AnalyticsSelectedViewId} from '../../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {AnalyticsReportsEnum} from '../../../../helpers/analyticsReportHelpers';
import {useAnalyticsTracking} from '../../../../helpers/tracking/useAnalyticsTracking';
import {analyticsLinksConfig} from '../../../reportNavigation/reportNavigationLinks';

/*
 * Constants.
 */

const maxRecipients = 100; // Just in case we limit to the first 100 recipients.

const frequencies = [
  {
    id: SchedulingFrequenciesEnum.DAILY,
    label: <Trans id="schedule-frequency-daily">Daily</Trans>,
  },
  {
    id: SchedulingFrequenciesEnum.WEEKLY,
    label: <Trans id="schedule-frequency-weekly">Weekly</Trans>,
  },
  {
    id: SchedulingFrequenciesEnum.MONTHLY,
    label: <Trans id="schedule-frequency-monthly">Monthly</Trans>,
  },
];

// All report types are not supported by default: they need dedicated handling in the BE.
const supportedStages = new Set([
  AnalyticsReportsEnum.OVERVIEW,
  AnalyticsReportsEnum.TEAM_PERF,
  AnalyticsReportsEnum.CONVERSATIONS,
  AnalyticsReportsEnum.TAGS,
  // TODO: AnalyticsReportTypeEnum.MESSAGE_TEMPLATES,
  AnalyticsReportsEnum.SLA,
  AnalyticsReportsEnum.CUSTOMER_EXPERIENCE,
]);

const availableStages = analyticsLinksConfig
  // Make sure we only consider report types that are supported.
  .filter(({type}) => supportedStages.has(type))
  // Format available stages.
  .map(({type, name}) => ({
    id: type,
    label: name,
  }));

// These will be the table columns displayed in the email for each table element.
const reportTableColumns = {
  [AnalyticsTableMetricsEnum.TEAM_CHANGES]: ['messages_sent'],
  [AnalyticsTableMetricsEnum.TEAM_PERFORMANCE]: [
    'avg_reply_time',
    'avg_first_reply_time',
    'avg_handle_time',
    'num_messages_sent',
  ],
  [AnalyticsTableMetricsEnum.INBOX_TABLE]: [
    'open_at_start',
    'new_direct_in_inbox',
    'moved_into_inbox',
    'reopened',
  ],
  [AnalyticsTableMetricsEnum.TAGS_ALL_TABLE]: [
    'num_active_segments',
    'pct_active_segments',
    'avg_reply_time',
    'avg_handle_time',
  ],
  [AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE]: [
    'num_active_segments',
    'num_added',
    'num_removed',
    'avg_tag_duration',
  ],
  [AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2]: ['sla_rule', 'sla_breaches', 'num_current_breach'],
  [AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2]: [
    'num_messages_sent',
    'avg_reply_time',
    'avg_total_reply_time',
    'avg_csat_survey_score',
  ],
};

/*
 * Props.
 */

interface ReportHeaderSaveViewDialogProps {
  onOkClick: Handler;
  onRequestClose: Handler;
  selectedViewId?: AnalyticsSelectedViewId;
}

/*
 * Styles.
 */

const StyledInputLabel = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  margin-top: 5px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  margin-bottom: 4px;

  span {
    color: ${(p) => p.theme.palette.red.shade40};
  }
`;

// Fake the BoxedInput on a div
const StyledBoxDiv = styled.div`
  display: flex;

  border-radius: 8px;
  margin: 12px 0 22px;
  padding: 3px 8px;
  min-height: 32px;
  color: ${boxedInputColorReaders.text};
  background-color: ${boxedInputColorReaders.background};

  &:focus-within,
  &[focus-within] {
    background-color: ${boxedInputColorReaders.backgroundFocused};
    box-shadow: 0 0 0 1px ${(p) => p.theme.palette.blue.shade40},
      inset 0 0 0 1px ${(p) => p.theme.palette.blue.shade40};
  }

  > * {
    flex: 1;
  }
`;

/*
 * Components.
 */

export const ReportHeaderScheduleViewDialog: FC<ReportHeaderSaveViewDialogProps> = ({
  onOkClick,
  onRequestClose,
}) => {
  const dispatch = useAppDispatch();

  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const selectedView = useAppSelector(getAnalyticsSelectedView);
  const reportType = useAppSelector(getAnalyticsReportType);
  const allMetricsToFetch = useAppSelector(getAllAnalyticsMetricsToFetch);
  const timezone = useAppSelector(makeGetTimezone());

  const trackAnalytics = useAnalyticsTracking();

  const [recipients, setRecipients] = useState(
    (selectedView?.schedulingConfig?.recipients || []).map(buildContact),
  );
  const [subject, setSubject] = useState(selectedView?.schedulingConfig?.subject);
  const [message, setMessage] = useState(selectedView?.schedulingConfig?.message);
  const [stages, setStages] = useState(
    selectedView?.schedulingConfig?.sections.map((section) => section.stage.split('-')[0]) || [reportType],
  );
  const [frequency, setFrequency] = useState(
    selectedView?.schedulingConfig?.frequency || SchedulingFrequenciesEnum.WEEKLY,
  );

  const [displayDeleteConfirmation, setDisplayConfirmationDialog] = useState(false);

  const addRecipients: AddRecipients = (recipientsToAdd) => {
    if (recipients.length < maxRecipients) {
      const newRecipients = uniqBy([...recipients, ...recipientsToAdd], (r) => `${r.role}${r.contact.uid}`);
      setRecipients(newRecipients);
    }
  };

  const removeRecipients: RemoveRecipients = (recipientsToRemove) => {
    const newRecipients = differenceWith(recipients, recipientsToRemove, isVisualRecipientEqualToHandle);
    setRecipients(newRecipients);
  };

  const scheduleView = () => {
    if (!recipients.length || !subject || !frequency || !selectedView || !stages.length) {
      return;
    }

    const schedulingConfig: SchedulingConfig = {
      recipients: recipients.map((recipient) => recipient.contact.handle).filter(isDefined),
      frequency,
      sections: availableStages
        .filter((availableStage) => stages.includes(availableStage.id)) // Using availableStages to always keep the same section order.
        .map(({id}) => ({
          stage: id,
          metrics:
            allMetricsToFetch[id]?.displayedMetrics.map((metric) => ({
              id: metric,
              columns: isMetricATableElement(metric) ? reportTableColumns[metric] : undefined,
            })) || [],
        })),
      message,
      subject,
      time_zone: timezone,
      // Using the first found stage as report type.
      reportType:
        availableStages.find((stage) => stages.includes(stage.id))?.id || AnalyticsReportsEnum.OVERVIEW,
    };

    dispatch(updateSavedViewAsyncAction({...selectedView, schedulingConfig}, namespace));

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_VIEW_SCHEDULE);

    onOkClick();
  };

  const onDeleteConfirmation = (hasConfirmed: boolean) => {
    setDisplayConfirmationDialog(false);
    if (hasConfirmed && selectedView) {
      dispatch(updateSavedViewAsyncAction({...selectedView, schedulingConfig: null}, namespace));

      onRequestClose();
    }
  };

  const keyHandlers = {
    [KeyboardActionsEnum.CONFIRM]: scheduleView,
    [KeyboardActionsEnum.CLOSE]: onRequestClose,
  };

  return (
    <>
      <Dialog>
        <KeyboardShortcuts handlers={keyHandlers}>
          <DialogHeader title={<Trans id="saved-view-schedule-confirmation-header">Schedule report</Trans>} />
          <DialogContent>
            {displayRecipientsSection(namespace, recipients, addRecipients, removeRecipients)}
            {displaySubjectSection(subject, setSubject)}
            {displayMessageSection(message, setMessage)}
            {displayStagesSection(stages, setStages)}
            {displayFrequencySection(frequency, setFrequency)}
          </DialogContent>
          <DialogButtonGroup>
            {selectedView?.schedulingConfig && (
              <Button onClick={() => setDisplayConfirmationDialog(true)} variant={ButtonVariantsEnum.DANGER}>
                <Trans id="saved-view-schedule-delete-button">Delete</Trans>
              </Button>
            )}
            <ButtonGroupSpacer />
            <Button onClick={onRequestClose}>
              <Trans id="saved-view-schedule-cancel-button">Cancel</Trans>
            </Button>
            <Button variant={ButtonVariantsEnum.PRIMARY} onClick={scheduleView}>
              {selectedView?.schedulingConfig ? (
                <Trans id="saved-view-schedule-update-button">Update</Trans>
              ) : (
                <Trans id="saved-view-schedule-confirm-button">Schedule</Trans>
              )}
            </Button>
          </DialogButtonGroup>
        </KeyboardShortcuts>
      </Dialog>
      {maybeDisplayDeleteConfirmationDialog(displayDeleteConfirmation, onDeleteConfirmation)}
    </>
  );
};

/*
 * Helpers.
 */

function displayRecipientsSection(
  namespace: string,
  recipients: ReadonlyArray<VisualRecipientModel>,
  addRecipients: AddRecipients,
  removeRecipients: RemoveRecipients,
) {
  return (
    <div>
      <StyledInputLabel>
        <Trans id="dialog-schedule-view-recipients-label">Recipients</Trans>&nbsp;<span>*</span>
      </StyledInputLabel>
      <StyledBoxDiv>
        <RecipientsEditorContainer
          source={ContactSourcesEnum.EMAIL}
          namespace={namespace}
          role={RecipientRolesEnum.TO}
          recipients={recipients}
          addRecipients={addRecipients}
          removeRecipients={removeRecipients}
        />
      </StyledBoxDiv>
    </div>
  );
}

function displaySubjectSection(
  subject: string | undefined,
  setSubject: Dispatch<SetStateAction<string | undefined>>,
) {
  return (
    <BoxedInput
      label={<Trans id="dialog-schedule-view-subject-label">Subject</Trans>}
      isRequired
      size={VisualSizesEnum.MEDIUM}
    >
      {(childProps) => (
        <BoxedInputElement
          {...childProps}
          value={subject}
          onChange={setSubject}
          type={InputTypesEnum.TEXT}
          size={VisualSizesEnum.MEDIUM}
        />
      )}
    </BoxedInput>
  );
}

function displayMessageSection(
  message: string | undefined,
  setMessage: Dispatch<SetStateAction<string | undefined>>,
) {
  return (
    <TextAreaBox
      size={VisualSizesEnum.SMALL}
      label={<Trans id="dialog-schedule-view-message-label">Message (optional)</Trans>}
      value={message}
      onChange={setMessage}
      shouldAllowResize
    />
  );
}

function displayStagesSection(stages: Array<string>, setStages: Dispatch<SetStateAction<Array<string>>>) {
  return (
    <DropdownBoxCoordinator
      label={<Trans id="dialog-schedule-view-stages-label">Select which reports to include</Trans>}
      isRequired
      value={availableStages
        .filter((stage) => stages.includes(stage.id))
        .map(({label, id}, i) => (
          <span key={id}>
            {label}
            {i < stages.length - 1 && ', '}
          </span>
        ))}
      renderDropdown={(popoverProps) => (
        <SimpleDropdown {...popoverProps}>
          {availableStages.map(({id, label}) => (
            <SimpleDropdownItem
              key={id}
              isChecked={stages.includes(id)}
              onSelect={(e: FrontEvent) => {
                e.preventDefault();
                if (stages.includes(id)) {
                  // We must at least select one report.
                  if (stages.length > 1) {
                    setStages(stages.filter((s) => s !== id));
                  }
                } else {
                  setStages(stages.concat(id));
                }
              }}
            >
              {label}
            </SimpleDropdownItem>
          ))}
        </SimpleDropdown>
      )}
    />
  );
}

function displayFrequencySection(
  frequency: SchedulingFrequenciesEnum,
  setFrequency: Dispatch<SetStateAction<SchedulingFrequenciesEnum>>,
) {
  return (
    <DropdownBoxCoordinator
      label={<Trans id="dialog-schedule-view-frequency-label">Frequency</Trans>}
      isRequired
      value={frequencies.find((f) => f.id === frequency)?.label}
      renderDropdown={(popoverProps) => (
        <SimpleDropdown {...popoverProps}>
          {frequencies.map((freq) => (
            <SimpleDropdownItem
              key={freq.id}
              isChecked={freq.id === frequency}
              onSelect={() => setFrequency(freq.id)}
            >
              {freq.label}
            </SimpleDropdownItem>
          ))}
        </SimpleDropdown>
      )}
    />
  );
}

function maybeDisplayDeleteConfirmationDialog(
  displayDeleteConfirmation: boolean,
  onDeleteConfirmation: HandlerOf<boolean>,
) {
  if (!displayDeleteConfirmation) {
    return null;
  }

  return (
    <OkCancelDialog
      title={<Trans id="saved-view-schedule-delete-title">Delete report schedule</Trans>}
      okButtonLabel={<Trans id="saved-view-schedule-delete-button">Delete</Trans>}
      onOkClick={() => onDeleteConfirmation(true)}
      onRequestClose={() => onDeleteConfirmation(false)}
    >
      <Trans id="saved-view-schedule-delete-body">
        You are about to delete the delivery schedule for your report. Are you sure?
      </Trans>
    </OkCancelDialog>
  );
}

function buildContact(handle: string): VisualRecipientModel {
  return {
    namespace: 'global',
    role: RecipientRolesEnum.TO,
    contact: buildContactForHandle(ContactSourcesEnum.EMAIL, handle),
  };
}

function isMetricATableElement(metric: AnalyticsMetricsKeys): metric is keyof typeof reportTableColumns {
  return metric in reportTableColumns;
}
